/**
 * Hook paginado server-side para itens da fila de lembretes
 * Utiliza a RPC list_reminder_queue_items com paginação offset-based
 * Segue padrão do useFlowResultsPaginated
 * 
 * @version 1.0.0
 * RELEASE: 2026-03-29 v1.0.0 - Criação do hook
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger condicional para debug
const logger = createLogger('ReminderQueueItems');

/** Filtros para a listagem de itens da fila */
export interface ReminderQueueFilters {
  /** Filtro por status (pending, sent, failed, cancelled, skipped, processing) */
  status?: string | null;
  /** Busca por nome ou telefone do contato */
  search?: string | null;
  /** v1.2.0: Filtros de data — Data Criação (de/até) */
  createdFrom?: string | null;
  createdTo?: string | null;
  /** v1.2.0: Filtros de data — Data Agendamento (de/até) */
  remindFrom?: string | null;
  remindTo?: string | null;
  /** v1.2.0: Filtros de data — Data Envio (de/até) */
  sentFrom?: string | null;
  sentTo?: string | null;
}

/** Item da fila de lembretes retornado pela RPC */
export interface ReminderQueueItem {
  id: string;
  status: string;
  remind_at: string;
  sent_at: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  inbox_name: string | null;
  message_text: string | null;
  template_name: string | null;
  error_details: string | null;
  attempts: number;
  entity_id: string;
  entity_type: string;
  created_at: string;
  conversation_id: string | null;
  total_count: number;
  /** Status de leitura da mensagem vinculada (pending, sent, delivered, read, failed) */
  message_read_status: string | null;
}

/** Parâmetros de paginação */
interface PaginationParams {
  page: number;
  pageSize: number;
}

/**
 * Hook para buscar itens da fila de lembretes com paginação server-side
 *
 * @param ruleId - ID da regra de lembrete (opcional quando se filtra por entidade)
 * @param filters - Filtros de status/busca/datas
 * @param pagination - Parâmetros de paginação
 * @param enabled - Se a query deve ser executada (lazy loading)
 * @param entityFilter - v1.1.0: filtro alternativo por entidade (ex.: lembretes de UMA cobrança)
 */
export function useReminderQueueItems(
  ruleId: string | undefined,
  filters: ReminderQueueFilters,
  pagination: PaginationParams,
  enabled: boolean = true,
  entityFilter?: { entityType: string; entityId: string } | null
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      'reminder-queue-items',
      ruleId,
      currentTenant?.id,
      filters,
      pagination,
      entityFilter?.entityType ?? null,
      entityFilter?.entityId ?? null,
    ],
    queryFn: async () => {
      logger.debug('Buscando itens da fila de lembretes:', { ruleId, filters, pagination, entityFilter });

      const { data, error } = await (supabase.rpc as any)(
        'list_reminder_queue_items',
        {
          p_rule_id: ruleId ?? null,
          p_page: pagination.page,
          p_page_size: pagination.pageSize,
          p_status: filters.status || null,
          p_search: filters.search || null,
          p_tenant_hint: currentTenant?.id || null,
          // v1.2.0: Filtros de data
          p_created_from: filters.createdFrom || null,
          p_created_to: filters.createdTo || null,
          p_remind_from: filters.remindFrom || null,
          p_remind_to: filters.remindTo || null,
          p_sent_from: filters.sentFrom || null,
          p_sent_to: filters.sentTo || null,
          // v1.3.0: Filtros por entidade (ex.: cobrança)
          p_entity_type: entityFilter?.entityType ?? null,
          p_entity_id: entityFilter?.entityId ?? null,
        }
      );

      if (error) {
        logger.error('Erro ao buscar itens da fila de lembretes:', error);
        throw error;
      }

      // Total count vem em cada row (pattern COUNT(*) OVER())
      const rawItems = (data || []) as ReminderQueueItem[];
      const totalCount = rawItems[0]?.total_count ?? 0;

      logger.debug(`Recebidos ${rawItems.length} itens, total: ${totalCount}`);

      return {
        items: rawItems,
        totalCount: Number(totalCount),
        totalPages: Math.ceil(Number(totalCount) / pagination.pageSize) || 1,
      };
    },
    enabled:
      !!currentTenant?.id &&
      enabled &&
      (!!ruleId || !!(entityFilter?.entityType && entityFilter?.entityId)),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });


  return {
    items: query.data?.items || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    /** Invalida cache para forçar refetch */
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['reminder-queue-items', ruleId, currentTenant?.id],
      });
    },
  };
}
